import ContentLoader from 'react-content-loader';
import {
  Control,
  Controller,
  DeepMap,
  FieldError,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Accordion, IAccordionData } from '../../../components/Accordion/Accordion';
import { HooksFormInput } from '../../../components/Inputs/FormInput/HooksFormInput';
import { Label } from '../../../components/Inputs/Label/Label';
import { Text } from '../../../components/Text/Text';
import { useClientHasRendered } from '../../../helpers/hooks';
import { useUserInfo } from '../../../helpers/hooks/useUserInfo';
import { GetFishingWaterMetadata } from '../../../interfaces/graphql';
import { getFishingWaterMetadataEntries } from '../helpers/getFishingWaterMetadataEntries';
import { FishingWater } from '../types/FishingWater';
import { SuggestedWaterFormState } from '../types/SuggestedWaterForm';

import { AdditionalInfoFields } from './AdditionalInfoFields';
import { MapWithMarker } from './Fields/MapWithMarker';
import { useGeolocation } from '../../../contexts/GeolocationContext/GeolocationContext';
import { DEFAULT_COORDINATES } from '../../../components/Maps/constants/constants';

interface SuggestionFieldsProps {
  register: UseFormRegister<SuggestedWaterFormState>;
  control: Control<SuggestedWaterFormState>;
  setValue: UseFormSetValue<SuggestedWaterFormState>;
  errors: DeepMap<SuggestedWaterFormState, FieldError>;
  watch: UseFormWatch<SuggestedWaterFormState>;
  allMetadata: GetFishingWaterMetadata | undefined;
  isLoadingMetaData: boolean;
  isNewForm?: boolean;
  existingData?: FishingWater;
  requiredFields?: string[];
}

export const SuggestionFields = ({
  register,
  control,
  setValue,
  errors,
  watch,
  allMetadata,
  isLoadingMetaData,
  isNewForm = false,
  existingData,
  requiredFields = [],
}: SuggestionFieldsProps) => {
  const { user } = useUserInfo();
  const clientHasRendered = useClientHasRendered();
  const currentUserEmail = user?.privateFields.email ?? '';

  const fishingWaterMetadataEntries = allMetadata
    ? getFishingWaterMetadataEntries({
        allMetadata,
        currentWaterMetadata: existingData?.metadata ?? [],
        watch,
        setValue,
        register,
      })
    : [];

  const getValidationRules = (fieldName: string) => {
    return {
      required: requiredFields.includes(fieldName) ? true : false,
    };
  };

  const { location: userLocation } = useGeolocation();
  const userCoordinates = userLocation?.coordinates ?? DEFAULT_COORDINATES;

  const SuggestionFormData: IAccordionData[] = [
    {
      id: 'nameAndLocation',
      title: 'Name and Location',
      body: (
        <>
          <HooksFormInput<SuggestedWaterFormState>
            id="newWaterName"
            label="Name"
            className="mb-4 font-semibold"
            register={register}
            inputProps={{
              placeholder: existingData?.name ?? 'Enter water name',
              ...getValidationRules('newWaterName'),
            }}
            registerOptions={{
              required: requiredFields.includes('newWaterName'),
            }}
          />

          <Label
            required={requiredFields.includes('position')}
            customStyles="font-semibold"
            htmlFor="location"
          >
            Location
          </Label>
          <Text variant="sm">
            {isNewForm
              ? 'Please drag the marker to where the water can be found'
              : 'If incorrect, please drag the marker to where the water can be found'}
          </Text>
          <Controller
            defaultValue={
              isNewForm ? userCoordinates : (existingData?.coordinates ?? DEFAULT_COORDINATES)
            }
            control={control}
            name="position"
            rules={getValidationRules('position')}
            render={({ field: { name } }) => {
              return (
                <MapWithMarker
                  name={name}
                  setValue={setValue}
                  coordinates={
                    isNewForm ? userCoordinates : (existingData?.coordinates ?? DEFAULT_COORDINATES)
                  }
                />
              );
            }}
          />
        </>
      ),
    },
    ...fishingWaterMetadataEntries,
    {
      id: 'otherInformation',
      title: 'Other information',
      body: (
        <AdditionalInfoFields
          register={register}
          control={control}
          setValue={setValue}
          fishingWater={
            existingData ?? {
              externalId: '',
              name: '',
              coordinates: { latitude: 0, longitude: 0 },
              metadata: [],
              url: '',
            }
          }
          isNewForm={isNewForm}
        />
      ),
    },
  ];

  if (!clientHasRendered) {
    return <></>;
  }

  return (
    <div>
      {isLoadingMetaData ? (
        <ContentLoader
          uniqueKey="suggestion-accordion-loader-key"
          style={{ width: '100%', height: '300px' }}
        >
          <rect x="0" y="0" width="100%" height="70px" />
          <rect x="0" y="75" width="100%" height="70px" />
          <rect x="0" y="150" width="100%" height="70px" />
          <rect x="0" y="225" width="100%" height="70px" />
        </ContentLoader>
      ) : (
        <>
          <Accordion
            className="border-twilight rounded-none border-b-2 border-solid py-2"
            buttonClassName="p-0"
            labelClassName="p-0"
            blocksData={SuggestionFormData}
            chevronAlignment="right"
            openFirstItemInitially={isNewForm}
          />
          <HooksFormInput<SuggestedWaterFormState>
            id="email"
            label="Your email"
            error={errors.email}
            aria-invalid={errors.email ? 'true' : 'false'}
            className="my-4 font-semibold"
            register={register}
            inputProps={{
              placeholder: 'billy.bass@gmail.com',
              defaultValue: currentUserEmail,
              readOnly: !!currentUserEmail,
              ...getValidationRules('email'),
            }}
          />
        </>
      )}
    </div>
  );
};
